import React from 'react';

import { Button, Col, List, Row, Space, Typography, Tag } from 'antd';

import { CheckCircleOutlined } from '@ant-design/icons';

import { MissingRequirementProductMessage } from './MissingRequirementProduct';

import { useValidateActiveProduct } from './hooks';
import { useCheckPaymentTerm } from '../cod-payment/hooks';
import { useCheckPermissions } from 'hooks/useCheckPermissions';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import { checkPassedRequirementProduct } from 'common/components/product-add/make-product-active/utils';
import { getErrorsForActiveProduct, getMetadataProduct } from './utils';

const { Title } = Typography;

export const ProductsActiveProcessing = ({ productIds, children, mode }) => {
  const hasBypassProductInternalPermission = useCheckPermissions([
    {
      action: ABILITY_ACTION.BYPASS,
      subject: ABILITY_SUBJECT.PRODUCT_INTERNAL_MAINTENANCE,
    },
  ]);

  const { validateProducts, handleRevalidateActiveProduct, passedProducts } =
    useValidateActiveProduct({
      productIds,
    });

  const { isCODTerm } = useCheckPaymentTerm();

  const totalProducts = validateProducts?.length ?? 0;
  const totalPassedProducts = passedProducts?.length;

  return (
    <>
      <List
        style={{ maxHeight: 460, minHeight: 460, marginBottom: 12 }}
        size='small'
        className='scroller'
        itemLayout='vertical'
        dataSource={validateProducts}
        renderItem={(item) => {
          const isZeroFee = !item?.fee;
          const canBypassCharge =
            hasBypassProductInternalPermission || isZeroFee;

          const errors = getErrorsForActiveProduct(item?.activeResult);
          const primaryErrors = item?.activeResult?.errors ?? [];
          const additionalRequirements =
            item?.activeResult?.additionalRequirements ?? {};

          const productData = item;

          const isPassedMinimumRequirements =
            checkPassedRequirementProduct(item);

          const isPaid = Boolean(item?.isPaid);

          return (
            <List.Item
              style={{ alignItems: 'baseline' }}
              key={item.productId}
              extra={
                <Space align='middle' gutter={[4]} style={{ height: '70px' }}>
                  <Button
                    type='secondary'
                    style={{ borderRadius: 4 }}
                    onClick={() => {
                      window.open(`/product/${item.productId}`, '_blank');
                    }}
                  >
                    Open
                  </Button>
                </Space>
              }
            >
              <List.Item.Meta
                avatar={null}
                title={
                  <>
                    <ListItemTitle productName={item.productName ?? 'N/A'}>
                      {canBypassCharge ? null : isPaid ? (
                        <StatusPaidProduct />
                      ) : (
                        <StatusUnpaidProduct />
                      )}
                    </ListItemTitle>
                  </>
                }
                description={
                  <>
                    <Row gutter={[0, 6]}>
                      <Col span={24}>
                        <Typography.Text strong>
                          {getMetadataProduct(item)}
                        </Typography.Text>
                      </Col>
                      <Col span={24}>
                        {isPassedMinimumRequirements ? (
                          <PassedProductMessage />
                        ) : (
                          <MissingRequirementProductMessage
                            errors={errors}
                            entityId={item?.productId}
                            entityType='Product'
                            onRevalidateActiveProduct={
                              handleRevalidateActiveProduct
                            }
                            primaryErrors={primaryErrors}
                            productData={productData}
                            mode={mode}
                            additionalRequirements={additionalRequirements}
                          />
                        )}
                      </Col>
                    </Row>
                  </>
                }
              />
            </List.Item>
          );
        }}
      />

      <Row align='middle' justify={isCODTerm ? 'end' : 'space-between'}>
        {isCODTerm ? null /* Update UI Later  */ : (
          <Col>
            <Typography.Text
              type='success'
              strong
              italic
              style={{ fontSize: 14 }}
            >
              <CheckCircleOutlined style={{ marginRight: 4 }} />
              {totalPassedProducts} / {totalProducts} product(s) ready to charge
            </Typography.Text>
          </Col>
        )}
        <Col>{children}</Col>
      </Row>
    </>
  );
};

const ListItemTitle = ({ productName, children }) => {
  return (
    <Row gutter={[8, 0]} align='middle'>
      <Col>
        <Title level={5} style={{ marginTop: 8 }} ellipsis strong>
          {productName}
        </Title>
      </Col>
      <Col>{children}</Col>
    </Row>
  );
};

export const StatusChargeProduct = ({ children, style, ...rest }) => {
  return (
    <Tag
      style={{
        borderRadius: 4,
        ...style,
      }}
      {...rest}
    >
      <Typography.Text style={{ color: '#fff' }}>{children}</Typography.Text>
    </Tag>
  );
};

export const StatusPaidProduct = () => {
  return <StatusChargeProduct color='#73d13d'>Paid</StatusChargeProduct>;
};

export const StatusUnpaidProduct = () => {
  return <StatusChargeProduct color='#faad14'>Unpaid</StatusChargeProduct>;
};

const PassedProductMessage = (props) => {
  return (
    <Typography.Text
      type='success'
      style={{ marginBottom: 0 }}
      strong
      {...props}
    >
      Validation successful
    </Typography.Text>
  );
};
